/**
 * SMPTE timecode arithmetic for the player's source-TC readout.
 *
 * Drop-frame timecode never drops frames, it drops frame NUMBERS: at
 * every minute boundary except each tenth minute, the first D numbers
 * are skipped (D = 2 at 29.97, D = 4 at 59.94), which keeps the label
 * clock aligned with wall time despite the 1000/1001 rate.
 */

export interface TimecodeSpec {
  /** Start timecode as recorded, e.g. "21:13:50;05". */
  start: string;
  /** Exact frame rate, e.g. 60000/1001 stored as 59.94. */
  frameRate: number;
  dropFrame: boolean;
}

/** Nominal integer rate a fractional rate is labelled with (59.94 -> 60). */
function nominalRate(frameRate: number): number {
  return Math.round(frameRate);
}

/** Frame numbers dropped per minute boundary under drop-frame rules. */
function dropCount(frameRate: number): number {
  // SMPTE 12M: 2 for 29.97, 4 for 59.94; scales with the nominal rate.
  return Math.round(nominalRate(frameRate) / 15);
}

/**
 * Parse "hh:mm:ss:ff" or drop-frame "hh:mm:ss;ff" to an absolute frame
 * count since midnight under the given rate's counting rules.
 */
export function timecodeToFrames(timecode: string, frameRate: number, dropFrame: boolean): number {
  const match = timecode.match(/^(\d{2}):(\d{2}):(\d{2})[:;](\d{2})$/);
  if (!match) {
    throw new TypeError(`unparseable timecode: ${JSON.stringify(timecode)}`);
  }
  const [, hh, mm, ss, ff] = match;
  const hours = Number(hh);
  const minutes = Number(mm);
  const seconds = Number(ss);
  const frames = Number(ff);
  const nominal = nominalRate(frameRate);
  if (minutes > 59 || seconds > 59 || frames >= nominal) {
    throw new RangeError(`timecode field out of range: ${timecode}`);
  }

  let total = ((hours * 3600 + minutes * 60 + seconds) * nominal) + frames;
  if (dropFrame) {
    const drop = dropCount(frameRate);
    const totalMinutes = hours * 60 + minutes;
    total -= drop * (totalMinutes - Math.floor(totalMinutes / 10));
  }
  return total;
}

/** Inverse of timecodeToFrames, emitting ; as the frame separator when drop-frame. */
export function framesToTimecode(frameCount: number, frameRate: number, dropFrame: boolean): string {
  if (!Number.isInteger(frameCount) || frameCount < 0) {
    throw new RangeError(`frame count must be a non-negative integer, got ${frameCount}`);
  }
  const nominal = nominalRate(frameRate);
  let frames = frameCount;

  if (dropFrame) {
    const drop = dropCount(frameRate);
    const framesPerMinute = 60 * nominal - drop;
    const framesPerTenMinutes = 10 * framesPerMinute + drop;
    const tenMinuteBlocks = Math.floor(frames / framesPerTenMinutes);
    const remainder = frames % framesPerTenMinutes;
    // The first minute of each ten-minute block keeps all its numbers,
    // so the drop applies only to the minutes after it.
    const extraMinutes = remainder >= drop ? Math.floor((remainder - drop) / framesPerMinute) : 0;
    frames += drop * (tenMinuteBlocks * 9 + extraMinutes);
  }

  const ff = frames % nominal;
  const totalSeconds = Math.floor(frames / nominal);
  const ss = totalSeconds % 60;
  const mm = Math.floor(totalSeconds / 60) % 60;
  const hh = Math.floor(totalSeconds / 3600) % 24;
  const pad = (value: number) => String(value).padStart(2, '0');
  const separator = dropFrame ? ';' : ':';
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}${separator}${pad(ff)}`;
}

/**
 * Source timecode label for a player position. Media time is converted
 * to elapsed frames at the exact rate, added to the recorded start.
 */
export function timecodeForSeconds(spec: TimecodeSpec, mediaSeconds: number): string {
  if (!Number.isFinite(mediaSeconds) || mediaSeconds < 0) {
    throw new RangeError(`mediaSeconds must be non-negative, got ${mediaSeconds}`);
  }
  const startFrames = timecodeToFrames(spec.start, spec.frameRate, spec.dropFrame);
  const elapsed = Math.floor(mediaSeconds * spec.frameRate + 1e-6);
  return framesToTimecode(startFrames + elapsed, spec.frameRate, spec.dropFrame);
}

/** Seconds per frame, for frame stepping. */
export function frameDurationSeconds(frameRate: number): number {
  return 1 / frameRate;
}
